import {useCallback, useMemo, useState} from 'react';
import {QueryFunctionContext, useQuery} from 'react-query';

import api from 'api';
import {DataItem, ListResponse, Vehicle} from 'types';
import {getFullName} from 'helpers/user';
import ViewDetails from 'components/ViewDetails';
import useSort from 'hooks/useSort';
import useDebounce from 'hooks/useDebounce';
import {useHistory} from 'react-router-dom';
import useAuth from 'hooks/useAuth';

async function getVehicles({queryKey}: QueryFunctionContext) {
  const [, order, keyWord] = queryKey;
  const {data} = await api.get<ListResponse<Vehicle>>('/vehicles/', {
    params: {order, search: keyWord ? keyWord : undefined},
  });

  return data;
}

function mapVehicles(vehiles: Vehicle[]): DataItem[] {
  return vehiles.map((v) => ({
    ...v,
    driver: getFullName(v.driver) || '-',
    vehicle_type: v.vehicle_type === 'car' ? 'Car' : 'Bike',
    view: <ViewDetails to={`/settings/vehicles/${v.id}`} />,
  }));
}

function useVehicles() {
  const {rolePermission} = useAuth();
  const history = useHistory();
  const [keyWord, setKeyWord] = useState(
    sessionStorage.getItem(`search_vehicle`) || ''
  );
  const {sortBy, sortOrder, order, onSort} = useSort('nickname');

  const searchDebounced = useDebounce(keyWord, 1000);

  const {data: vehicles, isLoading} = useQuery(
    ['vehicles', order, searchDebounced],
    getVehicles
  );

  const handleNavigateRequest = useCallback(
    (row: DataItem) => {
      history.push(`/settings/vehicles/${row.id}`);
    },
    [history]
  );

  const data = useMemo(
    function () {
      return vehicles ? mapVehicles(vehicles.results) : [];
    },
    [vehicles]
  );

  const canWrite = rolePermission['vehicles']?.permissions?.write;

  return {
    data,
    vehicles: vehicles?.results || [],
    isLoading,
    sortBy,
    sortOrder,
    onSort,
    keyWord,
    setKeyWord,
    canWrite: !!canWrite,
    handleNavigateRequest,
  };
}

export default useVehicles;
